class WriteInHighlighter extends HTMLElement {
  private paragraphs: NodeListOf<HTMLParagraphElement>;
  private throttledScroll: Function;

  constructor() {
    super();
    this.paragraphs = this.querySelectorAll('p');
    this.throttledScroll = this.throttle(this.handleScroll.bind(this), 200);
  }

  connectedCallback() {
    window.addEventListener('scroll', this.throttledScroll as EventListener);
    this.handleScroll();
  }

  /**
   * Checks if an element is within the viewport.
   * @param {HTMLElement} element - The element to check.
   * @returns {boolean} - True if the element is in the viewport, false otherwise.
   */
  private isInViewport(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  /**
   * Animates the words by adding highlight animation to each character.
   */
  private animateWords() {
    this.paragraphs.forEach((paragraph) => {
      const textContent = paragraph.textContent;

      if (textContent !== null) {
        const words = textContent.split(' ');

        const spannedText = words
          .map((word) => {
            if (word === '') return '';

            const characters = Array.from(word)
              .map((char) => `<write-in-char class="o-write-in__char">${char}</write-in-char>`)
              .join('');

            return `<write-in-word>${characters}</write-in-word>`;
          })
          .join(' ');

        paragraph.innerHTML = spannedText;

        const spanCharElements = paragraph.querySelectorAll('write-in-char');
        spanCharElements.forEach((span, index) => {
          setTimeout(() => {
            span.classList.add('is-highlighted');
          }, index * 50);
        });
      }
    });
  }

  /**
   * Handles the scroll event.
   * If the element is in the viewport, triggers the animation of words.
   * Removes the scroll event listener after animation completion.
   */
  private handleScroll = () => {
    if (this.isInViewport(this)) {
      this.animateWords();
      window.removeEventListener('scroll', this.throttledScroll as EventListener);
    }
  };

  /**
   * Throttles the function to limit the rate of function invocations.
   * @param {Function} fn - The function to throttle.
   * @param {number} delay - The delay between invocations in milliseconds.
   * @returns {Function} - The throttled function.
   */
  private throttle(fn: Function, delay: number): Function {
    let lastCallTime = 0;
    return function (this: any, ...args: any[]) {
      const now = new Date().getTime();
      if (now - lastCallTime >= delay) {
        fn.apply(this, args);
        lastCallTime = now;
      }
    };
  }
}

if(!customElements.get('write-in-highlighter')) {
  customElements.define('write-in-highlighter', WriteInHighlighter);
}